import { AlertCircle } from "lucide-react";
import { createContext, type ReactNode, useContext } from "react";
import { Icon } from "../../icons/Icon";
import { Button } from "../../primitives/Button";
import { Field, type FieldControlProps } from "../../primitives/Field";
import { cx } from "../../styles";
import { type ApiForm, FORM_ERROR } from "./useApiForm";

/**
 * The pieces a create-or-edit screen is assembled from.
 *
 * The layering is deliberate. `ui/primitives/Field` knows nothing about forms —
 * it generates ids, wires `aria-describedby` and renders an error, and the
 * gallery uses it on its own. {@link FormField} is the thin thing that binds a
 * primitive to a form: it reads the error from the form's state, registers its
 * input so a server rejection can move focus to it, and passes the rest
 * through. Nothing about a screen's layout lives in either.
 */

// biome-ignore lint/suspicious/noExplicitAny: the context holds whichever form the provider was given, and a generic React context cannot express "the same T the provider had". Every read goes through useFormContext, which narrows it once.
const FormContext = createContext<ApiForm<any> | null>(null);

/** The form this field belongs to. Throws outside a `<Form>`, which is a wiring bug. */
function useFormContext(): ApiForm<Record<string, unknown>> {
	const form = useContext(FormContext);
	if (form === null) {
		throw new Error("FormField must be used inside a <Form>");
	}
	return form as ApiForm<Record<string, unknown>>;
}

export interface FormProps<T extends object> {
	form: ApiForm<T>;
	children: ReactNode;
	className?: string;
	/** Names the form for assistive technology, when there is no heading above it. */
	"aria-label"?: string;
}

/**
 * A real `<form>`.
 *
 * Not a `<div>` with a button, and the difference is not pedantry: a form
 * submits on Enter from any input, which is how somebody fills one in without
 * reaching for the mouse, and it is announced as a form rather than as a group
 * of unrelated controls.
 */
export function Form<T extends object>({ form, children, className, ...rest }: FormProps<T>) {
	return (
		<FormContext.Provider value={form}>
			<form
				noValidate
				onSubmit={form.handleSubmit}
				className={cx("flex flex-col gap-5", className)}
				{...rest}
			>
				{children}
			</form>
		</FormContext.Provider>
	);
}

export interface FormFieldProps {
	/** The key in the form's values. Also what a server error names. */
	name: string;
	label: ReactNode;
	hint?: ReactNode;
	required?: boolean;
	/** Marks the field optional in the label, which is the honest default for most. */
	optional?: boolean;
	className?: string;
	children: (props: FieldControlProps & { name: string }) => ReactNode;
}

/**
 * A field bound to the form.
 *
 * Reads its own error rather than being handed one, so a screen never has to
 * write `error={form.errors.sku}` on eleven inputs and get the tenth wrong.
 */
export function FormField({ name, children, ...rest }: FormFieldProps) {
	const form = useFormContext();
	const error = form.errors[name];

	return (
		<Field {...rest} {...(error === undefined ? {} : { error })}>
			{(control) => (
				<div
					// The registration hangs off a wrapper rather than the control,
					// because the control is whatever the caller rendered — an input, a
					// combobox trigger, a group of radios — and only the DOM knows how
					// to find something focusable inside it.
					ref={(node) => form.registerField(name, focusableIn(node))}
				>
					{children({ ...control, name })}
				</div>
			)}
		</Field>
	);
}

/** The first thing inside a wrapper that focus can land on. */
function focusableIn(node: HTMLElement | null): HTMLElement | null {
	if (node === null) {
		return null;
	}
	return node.querySelector<HTMLElement>(
		"input, textarea, select, button, [tabindex]:not([tabindex='-1'])",
	);
}

export interface FormSectionProps {
	title?: ReactNode;
	description?: ReactNode;
	/** Lay the fields out in two columns above 640px. */
	columns?: 1 | 2;
	children: ReactNode;
	className?: string;
}

/** A titled group of fields. */
export function FormSection({
	title,
	description,
	columns = 1,
	children,
	className,
}: FormSectionProps) {
	return (
		<section className={cx("flex flex-col gap-3", className)}>
			{title !== undefined && (
				<div className="flex flex-col gap-0.5">
					<h3 className="text-[13px] font-bold text-fg">{title}</h3>
					{description !== undefined && (
						<p className="text-[11.5px] leading-relaxed text-fg-muted">{description}</p>
					)}
				</div>
			)}
			<div className={cx("grid gap-3", columns === 2 && "sm:grid-cols-2")}>{children}</div>
		</section>
	);
}

export interface FormActionsProps {
	children: ReactNode;
	className?: string;
}

/** The footer: actions, end-aligned, above a rule. */
export function FormActions({ children, className }: FormActionsProps) {
	return (
		<div className={cx("flex justify-end gap-2 border-t border-border pt-4", className)}>
			{children}
		</div>
	);
}

export interface FormErrorSummaryProps<T extends object> {
	/**
	 * The form whose banner to render.
	 *
	 * Generic rather than `ApiForm<object>`: `reset` takes a `T`, which makes
	 * `ApiForm` contravariant in it, so no single non-generic type accepts every
	 * form. An earlier draft wrote a union of three and it accepted none of
	 * them.
	 */
	form: ApiForm<T>;
	/** Translates a message key. Server messages arrive already translated. */
	t: (key: string) => string;
	/** Offered when the failure is one a reload would resolve. */
	onReload?: () => void;
	reloadLabel?: string;
}

/**
 * The banner for anything that is not one field's fault.
 *
 * `role="alert"`, because it appears after a submit somebody is waiting on and
 * may be above the fold they are looking at.
 *
 * The reload action is offered only for a version conflict, where it is the
 * one thing that helps: somebody else saved, and the form is arguing with a
 * record that no longer exists in the shape it was loaded in. Offering Reload
 * on a validation failure would invite people to throw away what they typed.
 */
export function FormErrorSummary<T extends object>({
	form,
	t,
	onReload,
	reloadLabel,
}: FormErrorSummaryProps<T>) {
	const message = (form as ApiForm<Record<string, unknown>>).errors[FORM_ERROR];
	if (message === undefined) {
		return null;
	}

	// A message key resolves through the bundle; anything else is the server's
	// own sentence, already in the locale the client asked for.
	const text = message.startsWith("errors.") ? t(message) : message;

	return (
		<div
			role="alert"
			className="flex items-start gap-2.5 rounded-sm border border-danger-border border-s-[3px] border-s-danger-fg bg-bg-surface p-3"
		>
			<Icon as={AlertCircle} size={17} className="mt-px shrink-0 text-danger-fg" />
			<p className="min-w-0 flex-1 text-[12.5px] leading-relaxed text-danger-fg">{text}</p>
			{form.staleVersion && onReload !== undefined && reloadLabel !== undefined && (
				<Button size="sm" variant="secondary" onClick={onReload}>
					{reloadLabel}
				</Button>
			)}
		</div>
	);
}
